<script>
import debounce from 'lodash/debounce';
import deburr from 'lodash/deburr';
import enterView from 'enter-view';
import findIndex from 'lodash/findIndex';
import VirtualList from 'vue-virtual-scroll-list';

import VirtualListItem from './VirtualScrollListItem.vue';
import { bus } from '../../main';

const ALPHABET = [...'#abcdefghijklmnopqrstuvwxyz'];

export default {
  name: 'VirtualScrollList',
  components: {
    'virtual-list': VirtualList,
  },
  props: {
    items: {
      type: Array,
      default: null,
    },
    searchConfig: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      active: null,
      bus,
      itemComponent: VirtualListItem,
      popup: null,
      search: '',
      target: null,
    };
  },
  computed: {
    filteredItems() {
      if (this.search.length >= 1) {
        const searchTerm = deburr(this.search.toLowerCase());
        return this.items.filter((item) => {
          let t = false;
          let d = false;

          // if item has a title, check if it contains the search term
          if (item.title) {
            t = deburr(item.title.toLowerCase()).includes(searchTerm);
          }

          // if item has a description, check if it contains the search term
          if (item.desc) {
            d = deburr(item.desc.toLowerCase()).includes(searchTerm);
          }

          return t || d;
        });
      }

      return this.items;
    },
    activeAlphabet() {
      return ALPHABET.filter((character) => this.filteredItems.some((item) => {
        const titleFirstCharacter = deburr(item.title[0].toLowerCase())[0];
        // if character is `#`, test if title first character is a number
        // else character is a letter of alphabet, test if title first character match the current character
        return (character === '#') ? !Number.isNaN(Number(titleFirstCharacter)) : titleFirstCharacter === character;
      }));
    },
    navItems() {
      return ALPHABET.map((character) => ({
        label: character,
        hasResults: this.activeAlphabet.includes(character.toLowerCase()),
      }));
    },
    orderedItems() {
      const separators = this.activeAlphabet.map((character) => ({ id: character, title: character, isLetter: true }));

      // mix separators and items together and sort them alphabetically
      return [...separators, ...this.filteredItems]
        .sort((a, b) => a.title.toLowerCase().localeCompare(b.title.toLowerCase()));
    },
    noResultsMessage() {
      return this.searchConfig && this.searchConfig.noResults
        ? this.searchConfig.noResults.replace('{0}', this.search)
        : '';
    },
  },
  watch: {
    active: {
      handler(val) {
        if (val && val.links && val.links.length === 1) {
          window.location = val.links[0].url;
        } else if (val != null) {
          this.showPopup(val);
        }
      },
    },
  },
  created() {
    window.addEventListener('resize', debounce(this.resizeHandler, 250));

    bus.$on('handle-virtual-scroll-list-item-click', (data) => {
      this.setActive(data.event, data.source);
    });
  },
  mounted() {
    this.setVars();

    this.stickyScroller = enterView({
      offset: 1,
      selector: '.js-sticky',
      enter: (e) => e.classList.add('has-reached-top'),
      exit: (e) => {
        e.classList.remove('has-reached-top');
        this.focusLetter = null;
      },
    });

    // Ensures elements with an ARIA role that require parent roles (listitem) are contained by them
    this.$el.querySelector('[role="group"]').setAttribute('role', 'list');
  },
  methods: {
    resizeHandler() {
      this.setVars();
    },
    setVars() {
      this.headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10);
      this.navHeight = 0;
      this.searchHeight = 0;

      if (this.$refs.navstretchbar && this.$refs.searchstretchbar) {
        this.navHeight = this.$refs.navstretchbar.getBoundingClientRect().height;
        this.searchHeight = this.$refs.searchstretchbar.getBoundingClientRect().height;
      }

      const max = document.body.clientWidth;
      document.documentElement.style.setProperty('--vw', `${max}px`);
    },
    setLetterFocus(letter) {
      const index = findIndex(this.orderedItems, (item) => item.isLetter && item.id === letter);
      this.$refs.vsl.scrollToIndex(index);
    },
    handleSearchFocus() {
      const firstLetter = document.querySelector('.alphabetical-list__nav__item.has-results a');
      if (!firstLetter) return;

      const letter = firstLetter.innerText.toLowerCase();
      this.setLetterFocus(letter);
    },
    setActive(e, item) {
      this.target = e.currentTarget;
      this.active = item;
    },
    showPopup(item) {
      this.popup = {
        title: item.popupTitle,
        desc: item.popupDesc,
        options: item.links,
      };

      this.$nextTick(() => this.$refs.popupOptions[0].focus());
    },
    closePopup() {
      this.popup = null;
      this.active = null;
      this.$nextTick(() => this.target.focus());
    },
    focusFirstPopupOption(e) {
      e.preventDefault();
      this.$refs.popupOptions[0].focus();
    },

    focusPopupClose(e) {
      if (e.target === this.$refs.popupOptions[0]) {
        e.preventDefault();
        this.$refs.popupClose.focus();
      }
    },
  },
};
</script>
<template>
  <div class="alphabetical-list__grid js-sticky">

    <!-- Anchors -->
    <nav
      v-if="searchConfig"
      :aria-label="search.ariaLabel"
      class="alphabetical-list__nav alphabetical-list__nav--virtual"
    >
      <div
        ref="navstretchbar"
        class="alphabetical-list__stretch-bar"
      >
        <div class="alphabetical-list__nav__wrapper">
          <ol class="alphabetical-list__nav__list">
            <template v-for="(navItem, index) in navItems">
              <li
                class="alphabetical-list__nav__item"
                :class="[navItem.hasResults === true ? 'has-results' : '']"
                :key="index"
              >
                <a
                  v-if="navItem.hasResults"
                  :href="'#' + navItem.label"
                  class="alphabetical-list__nav__link"
                  @click.prevent="setLetterFocus(navItem.label)"
                >
                  <span class="alphabetical-list__nav__link__text">{{ navItem.label }}</span>
                </a>
                <button
                  v-if="!navItem.hasResults"
                  class="alphabetical-list__nav__link"
                  disabled
                >
                  <span class="alphabetical-list__nav__link__text">{{ navItem.label }}</span>
                </button>
                <span class="alphabetical-list__nav__highlight">
                  <span class="alphabetical-list__nav__highlight__wrapper">{{ navItem.label }}</span>
                </span>
              </li>
            </template>
          </ol>
        </div>
      </div>
    </nav>

    <!-- Search -->
    <div
      v-if="searchConfig"
      ref="search"
      class="alphabetical-list__search"
    >
      <div
        ref="searchstretchbar"
        class="alphabetical-list__stretch-bar"
      >
        <div class="alphabetical-list__search__wrapper">
          <div class="textfield">
            <label>
              <span class="sr-only">{{ searchConfig.label }}</span>
              <input
                class="textfield__input"
                :placeholder="searchConfig.label"
                v-model="search"
                type="search"
                @focus="handleSearchFocus"
              >
            </label>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M23.3 20.7L17.6 15c1.2-1.8 1.8-4 1.6-6.2-.2-2.5-1.5-4.8-3.4-6.4C14 1 11.9.2 9.7.2 7.2.2 4.8 1.2 3 3 1.2 4.8.2 7.2.2 9.7s1 4.9 2.8 6.7c1.8 1.8 4.2 2.8 6.7 2.8 1.9 0 3.7-.6 5.2-1.6l5.6 5.6c.3.4.8.6 1.3.6h.1c.5 0 .9-.2 1.2-.5.4-.3.6-.8.6-1.3.1-.5-.1-1-.4-1.3zM9.7 3.9c3.2 0 5.8 2.6 5.8 5.8 0 3.2-2.6 5.8-5.8 5.8-3.2 0-5.8-2.6-5.8-5.8 0-1.5.6-3 1.7-4.1 1.1-1.1 2.5-1.7 4.1-1.7z"/></svg>
          </div>
        </div>
      </div>
    </div>

    <!-- Results -->
    <div
      ref="results"
      class="alphabetical-list__results"
    >
      <virtual-list
        v-if="orderedItems.length > 0"
        ref="vsl"
        :data-key="'id'"
        :data-sources="orderedItems"
        :data-component="itemComponent"
        :estimate-size="54"
        :keeps="40"
        :page-mode="true"
        :extra-props="{
          searchTerm: search,
        }"
        :wrap-class="'alphabetical-list__results__list'"
      />
      <div
        v-else
        class="alphabetical-list__results__none"
      >
        {{ noResultsMessage }}
      </div>
    </div>

    <!-- Aside -->
    <slot name="aside" />

    <!-- Popup -->
    <template id="alphabetical-list-popup">
      <div
        v-if="popup"
        class="popup"
        role="dialog"
        transition="modal"
        @keydown.esc="closePopup"
      >
        <span
          class="popup__overlay"
          @click="closePopup"
        />
        <div class="popup__wrapper theme--white">
          <div class="popup__content">
            <strong class="popup__title">
              {{ popup.title }}
            </strong>
            <div
              v-if="popup.desc"
              class="popup__desc"
            >
              {{ popup.desc }}
            </div>
          </div>

          <div class="alphabetical-list__options">
            <template v-for="(option, index) in popup.options">
              <a
                :href="option.url"
                class="btn btn--black"
                ref="popupOptions"
                :key="index"
                @keydown.shift.tab="focusPopupClose"
              >
                <span class="btn__content">
                  <span class="btn__text">
                    {{ option.name }}
                  </span>
                </span>
              </a>
            </template>
          </div>

          <button
            class="popup__close"
            @click="closePopup"
            @keydown.tab.exact="focusFirstPopupOption"
            ref="popupClose"
          >
            <span class="sr-only">Close</span>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 15.983"><path d="M1.772 0A1.774 1.774 0 00.537 3.046L5.491 8 .537 12.955a1.774 1.774 0 102.509 2.509L8 10.509l4.954 4.954a1.774 1.774 0 102.509-2.509L10.508 8l4.954-4.954A1.774 1.774 0 1012.954.538L7.999 5.492 3.045.537A1.774 1.774 0 001.772 0z"/></svg>
          </button>
        </div>
      </div>
    </template>
  </div>
</template>
